/**
 * Design tokens for the embedded cart.
 *
 * This module is the single source of every themeable visual value in `embed/cart/`.
 * It is deliberately framework-neutral — a plain object graph plus three pure
 * functions, with no dependency on MUI, `createTheme`, or any other UI library. The
 * public contract (the `--bec-*` custom properties and the `data-config.tokens`
 * object) is therefore unaffected if the embed later adopts a component library:
 * that would change who *consumes* tokens, not the tokens themselves.
 *
 * Tokens reach components through two channels, both fed from the same resolved
 * object (see `CartThemeProvider.js`):
 *
 *   1. Emotion's `ThemeProvider`, so `styled` callbacks can read `({ theme })`.
 *   2. CSS custom properties declared on the cart's root element, so an embedding
 *      site can override any of them from its own stylesheet.
 *
 * Components should reference tokens via `token()` below rather than reading
 * `theme` directly — that emits the `var(--bec-*, <default>)` form which wires up
 * both channels at once.
 *
 * `app/_theme/colors.js` is imported here and nowhere else in `embed/`, so brand
 * color values enter the embed through exactly one door.
 */
import { CART_GLOBAL_VARIABLE_NAME } from '../../../app/cart/constants.js';
import {
    blueDark,
    blueLight3,
    bouncieBlue,
    fadedBlack,
    gray2,
    grayLight1,
    softBlue,
    white,
    zachBlack,
} from '../../../app/_theme/colors.js';

/**
 * Default token values. The shape of this object *is* the public token schema:
 * `resolveTokens` derives its allowlist from these keys, so a host can only
 * override a group/key that actually exists here.
 *
 * Scope note: only values worth exposing to an embedding site live here. One-off
 * internal layout numbers (a 6px gap between an item's image and its text, say)
 * stay as literals in the component that owns them — promoting every number to a
 * token would produce a large API surface nobody asked for.
 */
export const defaultTokens = {
    color: {
        accent: bouncieBlue, // primary actions: checkout button, links, spinner arc
        accentHover: blueDark,
        accentSoft: blueLight3, // quantity stepper hover fill
        onAccent: white, // text/icons placed on top of `accent`
        text: zachBlack,
        textMuted: fadedBlack, // close button, remove button, stepper glyphs
        textTertiary: '#0A275C',
        surface: white, // cart panel background
        surfaceSubtle: '#F9FAFC', // the below-the-fold footer block
        surfaceInverse: zachBlack, // Prime Day / Big Deal Days Amazon button
        border: grayLight1, // panel outline
        borderSubtle: gray2, // quantity stepper outline
        borderSoft: softBlue, // dividers between summary rows
        borderFooter: '#E5E8ED', // top edge of the footer block
        borderAmazon: '#DFE1E5', // top edge of the Amazon link row
        spinnerTrack: grayLight1,
        highlightBackground: '#E5F7ED', // light green
        highlightForeground: '#12A150', // green
        highlightText: '#0D8C4A', //dark green
        detailCalloutBackground: '#F2F6FC',
    },
    icon: {
        bag: '#C7CEDA',
        checkmarkBackground: '#12A150',
        checkmarkForeground: white,
        close: '#3A4250',
        stepper: '#1F2430',
        trash: '#8A93A2',
    },
    font: {
        // The panel root sets a full stack; inner components use the shorter forms
        // so they inherit the root's fallbacks.
        stack: 'text-regular, Helvetica Neue, sans-serif',
        body: 'text-regular, sans-serif',
        bold: 'text-bold, sans-serif',
        heading: 'text-medium, sans-serif',
    },
    fontSize: {
        title: 24,
        large: 20,
        body: 16,
        small: 14,
        fine: 12,
        stepper: 18,
    },
    size: {
        panelWidth: 420,
        panelTop: 70, // distance from viewport top, clearing the host's header
        panelBreakHeight: 700, // below this viewport height the panel goes full-height
        stepperHeight: 20,
        stepperInputWidth: 34,
        itemImage: 64,
        paymentImage: 280,
    },
    radius: {
        panel: 10,
        button: 10,
        stepper: 8,
        description: 6,
    },
    space: {
        panelPaddingVertical: 18,
        panelPaddingHorizontal: 20,
        largeGap: 40,
        mediumGap: 20,
        smallGap: 10,
        tinyGap: 5,
    },
    shadow: {
        panel: '0px 0px 10px rgba(0,0,0,.15)',
    },
    zIndex: {
        // One more than bouncieweb's HeaderNavigation, so the panel sits above it.
        panel: 1001,
    },
};

// Groups whose numeric values are lengths, and so get a `px` suffix when
// serialized to CSS. Everything else is emitted verbatim — `zIndex` must stay
// unitless, and `color`/`font`/`shadow` values are already strings.
const LENGTH_GROUPS = new Set(['fontSize', 'size', 'radius', 'space']);

const CSS_VAR_PREFIX = '--bec'; // TODO: change?

const toKebabCase = (value) => value.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`);

/** e.g. ('color', 'accentHover') -> '--bec-color-accent-hover' */
const cssVarName = (group, key) => `${CSS_VAR_PREFIX}-${toKebabCase(group)}-${toKebabCase(key)}`;

const cssValue = (group, value) => {
    if (typeof value === 'number') {
        return LENGTH_GROUPS.has(group) ? `${value}px` : value;
    }

    return String(value);
};

/**
 * Flatten a token object into a CSS custom property declaration block.
 * @returns {Record<string, string>} e.g. `{ '--bec-color-accent': '#1F69F2', '--bec-size-panel-width': '450px' }`
 */
export const tokensToCssVars = (tokens) =>
    Object.entries(tokens).reduce((vars, [group, groupTokens]) => {
        Object.entries(groupTokens).forEach(([key, value]) => {
            vars[cssVarName(group, key)] = cssValue(group, value);
        });
        return vars;
    }, {});

/**
 * Resolve a token reference to a CSS value that honors both delivery channels.
 *
 * Emits `var(--bec-<group>-<key>, <resolved default>)`. The custom property comes
 * first so a host stylesheet wins; the resolved token is the fallback, which keeps
 * rendering correct even if the custom property never arrives (see the note about
 * `all: revert` in `CartThemeProvider.js`).
 *
 * @param {object} theme resolved token object, from Emotion's ThemeProvider
 * @param {string} path dot path into the token object, e.g. 'color.accent'
 */
export const token = (theme, path) => {
    const [group, key] = path.split('.');
    const value = theme?.[group]?.[key] ?? defaultTokens[group]?.[key];

    if (value === undefined) {
        console.warn(`[${CART_GLOBAL_VARIABLE_NAME}] Unknown design token "${path}"`);
        return 'unset';
    }

    return `var(${cssVarName(group, key)}, ${cssValue(group, value)})`;
};

/**
 * Numeric token accessor, for the handful of places that need to compute with a
 * token rather than hand it to CSS — media query bounds and `calc`-style math,
 * where `var()` either isn't allowed (`@media`) or would need extra plumbing.
 * Reads the resolved theme only, so a host's CSS-variable override does not reach
 * these; that is a deliberate limitation of overriding via CSS rather than
 * `data-config`, and it's why panel geometry is also exposed through `data-config`.
 */
export const tokenNumber = (theme, path) => {
    const [group, key] = path.split('.');
    return theme?.[group]?.[key] ?? defaultTokens[group]?.[key];
};

const isValidLeaf = (value) =>
    typeof value === 'string' || typeof value === 'number' && Number.isFinite(value);

/**
 * Merge host-supplied token overrides over the defaults.
 *
 * Overrides are validated against an allowlist derived from `defaultTokens`:
 * unknown groups, unknown keys within a known group, and non-primitive leaf values
 * are each dropped with a warning. This keeps the public token contract to a fixed
 * set of *values* — a host cannot inject arbitrary structure, and a typo surfaces
 * in the console instead of silently doing nothing.
 *
 * @param {object} [overrides] the `tokens` object from the script tag's `data-config`
 */
export const resolveTokens = (overrides) => {
    if (!overrides || typeof overrides !== 'object') {
        return defaultTokens;
    }

    return Object.entries(defaultTokens).reduce((resolved, [group, groupDefaults]) => {
        const groupOverrides = overrides[group];
        resolved[group] = { ...groupDefaults };

        if (groupOverrides && typeof groupOverrides === 'object') {
            Object.entries(groupOverrides).forEach(([key, value]) => {
                if (!Object.hasOwn(groupDefaults, key)) {
                    console.warn(`[${CART_GLOBAL_VARIABLE_NAME}] Ignoring unknown token "${group}.${key}"`);
                    return;
                }
                if (!isValidLeaf(value)) {
                    console.warn(`[${CART_GLOBAL_VARIABLE_NAME}] Ignoring non-primitive token "${group}.${key}"`);
                    return;
                }
                resolved[group][key] = value;
            });
        }

        return resolved;
    }, {});
};

// Warn about whole groups the host got wrong, which `resolveTokens` would otherwise
// skip over silently because it iterates the defaults rather than the overrides.
export const warnUnknownTokenGroups = (overrides) => {
    if (!overrides || typeof overrides !== 'object') {
        return;
    }
    Object.keys(overrides)
        .filter((group) => !Object.hasOwn(defaultTokens, group))
        .forEach((group) => {
            console.warn(`[${CART_GLOBAL_VARIABLE_NAME}] Ignoring unknown token group "${group}"`);
        });
};
